import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  orderBy,
} from 'firebase/firestore';
import { db } from './firebase';

/** Load all interest registrations ordered by registered_at DESC. */
export async function loadInterestRegistrations() {
  const q = query(
    collection(db, 'interest_registrations'),
    orderBy('registered_at', 'desc'),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Recalculate interest counts by aggregating registrations and comparing
 * against current Firestore club/event interest_count values.
 * Returns an array of { type, id, current, calculated, diff }.
 */
export async function recalculateInterestCounts() {
  const regs = await loadInterestRegistrations();

  const clubCounts = {};
  const eventCounts = {};
  for (const d of regs) {
    if (d.club_id && d.club_id !== 'other') {
      clubCounts[d.club_id] = (clubCounts[d.club_id] || 0) + 1;
    }
    if (d.club_id && d.event_id) {
      const key = `${d.club_id}/${d.event_id}`;
      eventCounts[key] = (eventCounts[key] || 0) + 1;
    }
  }

  const fetches = [];
  for (const clubId of Object.keys(clubCounts)) {
    fetches.push(
      getDoc(doc(db, 'clubs', clubId)).then((snap) => ({
        type: 'club',
        id: clubId,
        current: (snap.exists() && snap.data().interest_count) || 0,
      })),
    );
  }
  for (const key of Object.keys(eventCounts)) {
    const [clubId, eventId] = key.split('/');
    fetches.push(
      getDoc(doc(db, 'clubs', clubId, 'events', eventId)).then((snap) => ({
        type: 'event',
        id: key,
        current: (snap.exists() && snap.data().interest_count) || 0,
      })),
    );
  }

  const results = await Promise.all(fetches);
  const preview = results.map((r) => {
    const calculated = r.type === 'club' ? clubCounts[r.id] : eventCounts[r.id];
    return { ...r, calculated, diff: calculated - r.current };
  });
  preview.sort((a, b) => b.diff - a.diff);
  return preview;
}

/**
 * Apply recalculated interest counts to Firestore.
 * @param {Array} preview - from recalculateInterestCounts()
 * @param {boolean} onlyIncreases - if true, only apply rows where diff > 0
 * @returns {number} count of updates applied
 */
export async function applyInterestCounts(preview, onlyIncreases) {
  const toUpdate = preview.filter((row) =>
    onlyIncreases ? row.diff > 0 : row.diff !== 0,
  );
  const promises = toUpdate.map((row) => {
    if (row.type === 'club') {
      return updateDoc(doc(db, 'clubs', row.id), {
        interest_count: row.calculated,
      });
    }
    const [clubId, eventId] = row.id.split('/');
    return updateDoc(doc(db, 'clubs', clubId, 'events', eventId), {
      interest_count: row.calculated,
    });
  });
  await Promise.all(promises);
  return toUpdate.length;
}
